"use client"

import React from "react"
import { useState, useEffect, useCallback } from "react"
import { Link, useLocation } from "react-router-dom"
import { Menu, X, ChevronDown, ChevronUp } from "lucide-react"
import { Outlet } from "react-router-dom"
import logo from "../assets/bioskar brand/bs-logo-favicon.png";
import ThemeToggle from "./ui/ThemeToggle";

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isResourcesOpen, setIsResourcesOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const location = useLocation()

  // Function to check if a route is active
  const isActive = useCallback((path: string) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }, [location.pathname])

  // Function to check if a resource sub-route is active
  const isResourceActive = (path: string) => {
    return location.pathname.includes(path)
  }

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 1024)
    }

    checkIfMobile()
    
    window.addEventListener("resize", checkIfMobile)

    // Cleanup
    return () => window.removeEventListener("resize", checkIfMobile)
  }, [])

  // Close mobile menu when switching to desktop
  useEffect(() => {
    if (!isMobile) {
      setIsMenuOpen(false)
    }
    
  }, [isMobile])

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "auto"
    }

    return () => {
      document.body.style.overflow = "auto"
    }
  }, [isMenuOpen])

  // Auto-open resources dropdown in mobile view if on a resources page
  useEffect(() => {
    if (isActive('/resources') && isMobile) {
      setIsResourcesOpen(true)
    }
  }, [location.pathname, isMobile, isActive])

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const toggleResources = () => {
    setIsResourcesOpen(!isResourcesOpen)
  }

  return (
    <nav className="flex items-center justify-between px-4 sm:px-6 md:px-8 py-3 sm:py-4 bg-white dark:bg-gray-900 shadow dark:shadow-gray-800 relative z-50 transition-colors">
      {/* Left Side - Brand Name */}
      <Link 
        to="/" 
        className="flex items-center text-lg sm:text-xl font-bold text-gray-800 dark:text-white transition-colors"
        aria-label="Bioskar Solutions Home"
      >
        <img src={logo} alt="Bioskar Solutions Logo" className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 mr-2 flex-shrink-0" loading="eager" fetchPriority="high" decoding="async" />
        <span className="hidden xs:inline sm:inline truncate">Bioskar Solutions</span>
      </Link>

      {/* Mobile Menu Button */}
      <button
        className="lg:hidden text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 rounded p-2 min-h-[44px] min-w-[44px] flex items-center justify-center transition-colors"
        onClick={toggleMenu}
        aria-label={isMenuOpen ? "Close menu" : "Open menu"}
        aria-expanded={isMenuOpen}
        aria-controls="mobile-menu"
      >
        {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Desktop Navigation */}
      <ul className="hidden lg:flex space-x-6 text-gray-600 dark:text-gray-300 items-center transition-colors" role="navigation" aria-label="Main navigation">
        <li>
          <Link 
            to="/" 
            className={`transition-colors ${isActive('/') ? "text-primary font-bold " : "hover:text-blue-600 dark:hover:text-blue-400"}`}
            aria-current={isActive('/') ? 'page' : undefined}
          >
            Home
          </Link>
        </li>
        <li>
          <Link 
            to="/about" 
            className={`transition-colors ${isActive('/about') ? "text-primary font-bold" : "hover:text-blue-600"}`}
            aria-current={isActive('/about') ? 'page' : undefined}
          >
            About Us
          </Link>
        </li>
        <li>
          <Link 
            to="/services" 
            className={`transition-colors ${isActive('/services') ? "text-primary font-bold" : "hover:text-blue-600"}`}
            aria-current={isActive('/services') ? 'page' : undefined}
          >
            Products & Services
          </Link>
        </li>
        <li>
          <Link 
            to="/industries" 
            className={`transition-colors ${isActive('/industries') ? "text-primary font-bold" : "hover:text-blue-600"}`}
          >
            Industries
          </Link>
        </li>
        <li className="relative group">
          <button className={`flex items-center transition-colors ${isActive('/resources') ? "text-primary font-bold" : "hover:text-blue-600"}`}>
            Resources
            <ChevronDown className="ml-1 h-4 w-4 group-hover:hidden" />
            <ChevronUp className="ml-1 h-4 w-4 hidden group-hover:block" />
          </button>
          <ul className="absolute left-0 mt-2 w-56 bg-white border rounded shadow opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-all duration-300  z-50">
            {/* <li>
              <Link 
                to="/resources/blogs" 
                className={`block px-4 py-2 hover:bg-gray-100 ${isResourceActive('/resources/blogs') ? "text-primary bg-gray-50" : ""}`}
              >
                Blogs & Articles
              </Link>
            </li> */}
            <li>
              <Link 
                to="/resources/faqs" 
                className={`block px-4 py-2 hover:bg-gray-100 ${isResourceActive('/resources/faqs') ? "text-primary bg-gray-50" : ""}`}
              >
                FAQs
              </Link>
            </li>
            <li>
            {/*  <Link 
                to="/resources/guides" 
                className={`block px-4 py-2 hover:bg-gray-100 ${isResourceActive('/resources/guides') ? "text-primary bg-gray-50" : ""}`}
              >
                Guides & Tutorials
              </Link> */}
            </li>
            <li>
              <Link 
                to="/resources/partnership" 
                className={`block px-4 py-2 hover:bg-gray-100 ${isResourceActive('/resources/partnership') ? "text-primary bg-gray-50" : ""}`}
              >
                Partnership & Info
              </Link>
            </li>
          </ul>
        </li>
        <li>
          <Link 
            to="/contact" 
            className={`transition-colors ${isActive('/contact') ? "text-primary font-medium" : "hover:text-blue-600"}`}
          >
            Contact
          </Link>
        </li>
      </ul>

      {/* Desktop CTA Button */}
      <div className="hidden lg:flex items-center gap-3">
        <ThemeToggle />
        <Link
          to="/contact"
          className={`bg-primary font-general text-white px-4 py-2 rounded hover:bg-secondary transition-colors ${isActive('/get-started') ? "bg-secondary" : ""}`}
        >
          Get Started
        </Link>
      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300" onClick={toggleMenu}></div>}

      {/* Mobile Menu */}
      <div
        className={`lg:hidden fixed top-0 right-0 h-full w-[85%] max-w-sm bg-white dark:bg-gray-900 shadow-2xl dark:shadow-gray-800 z-50 transform transition-all duration-300 ease-in-out ${
          isMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex justify-between items-center p-4 sm:p-5 border-b dark:border-gray-700 bg-white dark:bg-gray-900">
          <div className="font-bold text-lg sm:text-xl text-gray-800 dark:text-white">Menu</div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <button 
              onClick={toggleMenu} 
              className="text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded p-2 min-h-[44px] min-w-[44px] flex items-center justify-center transition-colors"
              aria-label="Close menu"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto h-[calc(100%-72px)] sm:h-[calc(100%-80px)] bg-white dark:bg-gray-900">
          <ul className="py-2">
            <li>
              <Link 
                to="/" 
                className={`block px-5 py-4 hover:bg-gray-100 dark:hover:bg-gray-800 text-base min-h-[48px] flex items-center transition-colors ${isActive('/') && location.pathname === '/' ? "text-primary font-medium bg-gray-50 dark:bg-gray-800" : "text-gray-600 dark:text-gray-300"}`} 
                onClick={toggleMenu}
              >
                Home
              </Link>
            </li>
            <li>
              <Link 
                to="/about" 
                className={`block px-5 py-4 hover:bg-gray-100 dark:hover:bg-gray-800 text-base min-h-[48px] flex items-center transition-colors ${isActive('/about') ? "text-primary font-medium bg-gray-50 dark:bg-gray-800" : "text-gray-600 dark:text-gray-300"}`} 
                onClick={toggleMenu}
              >
                About Us
              </Link>
            </li>
            <li>
              <Link 
                to="/services" 
                className={`block px-5 py-4 hover:bg-gray-100 dark:hover:bg-gray-800 text-base min-h-[48px] flex items-center transition-colors ${isActive('/services') ? "text-primary font-medium bg-gray-50 dark:bg-gray-800" : "text-gray-600 dark:text-gray-300"}`} 
                onClick={toggleMenu}
              >
                Products & Services
              </Link>
            </li>
            <li>
              <Link 
                to="/industries" 
                className={`block px-5 py-4 hover:bg-gray-100 dark:hover:bg-gray-800 text-base min-h-[48px] flex items-center transition-colors ${isActive('/industries') ? "text-primary font-medium bg-gray-50 dark:bg-gray-800" : "text-gray-600 dark:text-gray-300"}`} 
                onClick={toggleMenu}
              >
                Industries
              </Link>
            </li>
            <li onClick={toggleResources}>
              <button
                className={`flex items-center justify-between w-full px-5 py-4 hover:bg-gray-100 dark:hover:bg-gray-800 text-base min-h-[48px] transition-colors ${
                  isActive('/resources') ? "text-primary font-medium bg-gray-50 dark:bg-gray-800" : "text-gray-600 dark:text-gray-300"
                }`}
              >
                Resources
                {isResourcesOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </button>

              {isResourcesOpen && (
                <ul className="bg-gray-50 dark:bg-gray-800 py-2">
                  {/* <li>
                    <Link
                      to="/resources/blogs"
                      className={`block px-8 py-2 hover:bg-gray-100 ${
                        isResourceActive('/resources/blogs') ? "text-primary font-medium bg-gray-100" : "text-gray-600"
                      }`}
                      onClick={toggleMenu}
                    >
                      Blogs & Articles
                    </Link>
                  </li> */}
                  <li>
                    <Link
                      to="/resources/faqs"
                      className={`block px-8 py-2 hover:bg-gray-100 ${
                        isResourceActive('/resources/faqs') ? "text-primary font-medium bg-gray-100" : "text-gray-600"
                      }`}
                      onClick={toggleMenu}
                    >
                      FAQs
                    </Link>
                  </li>
                {/*  <li>
                    <Link
                      to="/resources/events"
                      className={`block px-8 py-2 hover:bg-gray-100 ${
                        isResourceActive('/resources/events') ? "text-primary font-medium bg-gray-100" : "text-gray-600"
                      }`}
                      onClick={toggleMenu}
                    >
                      Events & Webinars
                    </Link> 
                  </li> */}
                  <li>
                    <Link
                      to="/resources/partnership"
                      className={`block px-8 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 ${
                        isResourceActive('/resources/partnership') ? "text-primary font-medium bg-gray-100 dark:bg-gray-700" : "text-gray-600 dark:text-gray-300"
                      }`}
                      onClick={toggleMenu}
                    >
                      Partnership & Info
                    </Link>
                  </li>
                </ul>
              )}
            </li>
            <li>
              <Link 
                to="/contact" 
                className={`block px-5 py-4 hover:bg-gray-100 dark:hover:bg-gray-800 text-base min-h-[48px] flex items-center transition-colors ${isActive('/contact') ? "text-primary font-medium bg-gray-50 dark:bg-gray-800" : "text-gray-600 dark:text-gray-300"}`} 
                onClick={toggleMenu}
              >
                Contact
              </Link>
            </li>
            <li className="px-5 py-3">
              <Link
                to="/contact"
                className={`block w-full bg-primary font-general text-white px-4 py-3 rounded text-center hover:bg-secondary transition-colors min-h-[48px] flex items-center justify-center ${
                  isActive('/get-started') ? "bg-secondary" : ""
                }`}
                onClick={toggleMenu}
              >
                Get Started
              </Link>
            </li>
          </ul>
        </div>
      </div>

      <Outlet />
    </nav>
  )
}

export default React.memo(Navbar)